// Minimal session context. The shell loads /me on mount (the session
// cookie is HttpOnly so we can't read it directly); a 200 means the
// user is logged in and we have an Actor, a 401 means we render the
// login form. signIn and signOut update the context synchronously so
// the consuming routes don't have to re-fetch on transition.

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Client,
  type Me,
  UnauthorizedError,
  isApiError,
} from "@sms/sdk";

interface AuthState {
  state: "loading" | "anon" | "authed";
  me?: Me;
}

interface AuthContextValue {
  auth: AuthState;
  signIn: (me: Me) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  client: Pick<Client, "me" | "logout">;
  children: ReactNode;
  /** Skip the initial /me probe — used in tests to avoid network. */
  skipBoot?: boolean;
}

export function AuthProvider({ client, children, skipBoot = false }: AuthProviderProps) {
  const [auth, setAuth] = useState<AuthState>({
    state: skipBoot ? "anon" : "loading",
  });

  useEffect(() => {
    if (skipBoot) return;
    let cancelled = false;
    client
      .me()
      .then((me) => {
        if (!cancelled) setAuth({ state: "authed", me });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof UnauthorizedError || (isApiError(err) && err.status === 401)) {
          setAuth({ state: "anon" });
        } else {
          // Any other failure (network, 5xx) — surface as anon for now
          // so the user lands on the login screen rather than a
          // permanent spinner. A future polish round routes this to
          // an error boundary.
          setAuth({ state: "anon" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, skipBoot]);

  const signIn = useCallback((me: Me) => {
    setAuth({ state: "authed", me });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await client.logout();
    } catch {
      // Logout that fails on the server is still a logout on the client.
    }
    setAuth({ state: "anon" });
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({ auth, signIn, signOut }),
    [auth, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth: AuthProvider missing in the tree");
  return v;
}
